// Topic API 操作
import { AxiosInstance } from "axios";
import { Topic } from "../types";
import { ChatListItem } from "../types";
import { getTopicInfoTags } from "../utils/tags";

// API 相关类型
export interface TopicApiResponse {
  records: Array<Topic>;
  total: number;
  size: number;
  pages: number;
  current: number;
}

export interface CreateTopicRequest {
  topicName: string;
  type?: string;
  status: number;
}

export interface UpdateTopicRequest {
  topicName: string;
  type?: string;
  status: number;
}

/**
 * 获取 Topic 列表
 * @param axios axios 实例
 * @param pageSize 页面大小
 * @param pageNum 页码
 */
export async function fetchTopics(
  axios: AxiosInstance,
  pageSize: number = 20,
  pageNum: number = 0
): Promise<ChatListItem[]> {
  try {
    const response = await axios.get<TopicApiResponse>(`/api/dataservice/v1/ai-chat/topics`, {
      params: {
        pageSize,
        pageNum,
      },
    });

    const hasMore = response.data.current !== response.data.pages;

    // 转换 API 响应为前端 Topic 格式
    const topics: ChatListItem[] = response.data.records.map((record) => {
      const accountTags = getTopicInfoTags(record);

      return {
        id: record.id,
        topicName: record.topicName,
        type: record.type,
        status: record.status,
        createdDate: record.createdDate,
        lastModifiedDate: record.lastModifiedDate,
        ...(record.mentions !== undefined ? { mentions: record.mentions } : {}),
        ...(accountTags.length > 0 ? { accountTags } : {}),
        hasMore,
        // active: false, // 默认不激活
      };
    });

    return topics;
  } catch (error) {
    console.error("Failed to fetch topics:", error);
    throw error;
  }
}

/**
 * 创建新 Topic
 * @param axios axios 实例
 * @param data 创建数据
 */
export async function createTopic(
  axios: AxiosInstance,
  data: CreateTopicRequest
): Promise<ChatListItem> {
  try {
    const response = await axios.post<{
      createdDate: string;
      lastModifiedDate: string;
      id: string;
      topicName: string;
      type: string | null;
      status: number;
    }>(`/api/dataservice/v1/ai-chat/topics`, data);

    // 转换响应为 Topic 格式
    const topic: ChatListItem = {
      id: response.data.id,
      topicName: response.data.topicName,
      type: response.data.type,
      status: response.data.status,
      createdDate: response.data.createdDate,
      lastModifiedDate: response.data.lastModifiedDate,
    };

    return topic;
  } catch (error) {
    console.error("Failed to create topic:", error);
    throw error;
  }
}

/**
 * 更新 Topic
 * @param axios axios 实例
 * @param id Topic ID
 * @param data 更新数据
 */
export async function updateTopic(
  axios: AxiosInstance,
  id: string,
  data: UpdateTopicRequest
): Promise<ChatListItem> {
  try {
    const response = await axios.put<{
      createdDate: string;
      lastModifiedDate: string;
      id: string;
      topicName: string;
      type: string | null;
      status: number;
    }>(`/api/dataservice/v1/ai-chat/topics/${id}`, data);

    const topic: ChatListItem = {
      id: response.data.id,
      topicName: response.data.topicName,
      type: response.data.type,
      status: response.data.status,
      createdDate: response.data.createdDate,
      lastModifiedDate: response.data.lastModifiedDate,
      // 注意：不包含 active 字段，让 reducer 保留原有的 active 状态
    };

    return topic;
  } catch (error) {
    console.error("Failed to update topic:", error);
    throw error;
  }
}

/**
 * 删除 Topic (设置 status 为 0)
 * @param axios axios 实例
 * @param id Topic ID
 */
export async function deleteTopic(axios: AxiosInstance, id: string): Promise<void> {
  try {
    await axios.delete(`/api/dataservice/v1/ai-chat/topics/${id}`);
  } catch (error) {
    console.error("Failed to delete topic:", error);
    throw error;
  }
}
